import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';

import { CurrencyDisplay } from '@flipster/features/src/misc/display';
import {
  earnSummaryBunja,
  getEarnSummaryOfCurrency,
  usdBalanceEmptyThreshold,
} from '@flipster/features/src/shared/earn/state';
import { useOverlayItem } from '@flipster/overlay';
import { Button } from '@flipster/react/design-system';
import { Modal } from '@flipster/react/design-system-legacy/modal';
import Formatted from '@flipster/react/design-utils/formatted';
import { walletsBunja } from '@flipster/socket/r230522/wallet';
import { css } from '@flipster/styled-system/css';
import { hstack, stack } from '@flipster/styled-system/patterns';
import { PusdtCurrency, UsdtCurrency } from '@flipster/utils/constants/currency';
import Num from '@flipster/utils/num';

import { useWalletModal } from '@web/app/[locale]/(web)/_modal/WalletModal/hooks/useWalletModal';

const AccessPremiumEarnModal = () => {
  const { onClose } = useOverlayItem();
  const { openWalletModal } = useWalletModal();
  const { walletsAtom } = useBunja(walletsBunja);
  const { earnSummaryAtom } = useBunja(earnSummaryBunja);

  const wallets = useAtomValue(walletsAtom);
  const earnSummary = useAtomValue(earnSummaryAtom);

  const usdtWalletBalance = Num(wallets?.[UsdtCurrency]?.walletBalanceExclWithdrawalPending ?? 0);
  const pusdtBaseAprPct = getEarnSummaryOfCurrency(PusdtCurrency, earnSummary).baseAprPct;

  return (
    <Modal>
      <Modal.Close onClose={onClose} />
      <Modal.Header>
        <Modal.Title
          label={
            <div className={css({ textStyle: 'Text/L/Strong' })}>
              Deposit USDT to access
              <br />
              Premium Earn
            </div>
          }
        />
      </Modal.Header>
      <Modal.Content>
        <div className={stack({ gap: 16 })}>
          <div
            className={stack({
              p: 16,
              gap: 16,
              border: '1px solid',
              borderColor: 'border.default.level-1',
              borderRadius: 8,
            })}
          >
            <div className={hstack({ gap: 16, color: 'content.default.level-4' })}>
              <CurrencyDisplay
                currency={UsdtCurrency}
                icon={<CurrencyDisplay.Icon size={32} space={16} />}
                mainLabel={<CurrencyDisplay.Label value='shortName' css={{ textStyle: 'Text/L/Strong' }} />}
              />
              <Formatted.Number
                value={usdtWalletBalance}
                toFixed
                precision={8}
                css={{ flex: 1, textAlign: 'right', textStyle: 'Text/L/Monospace/Strong' }}
                suffix={UsdtCurrency}
              />
            </div>
            <div
              className={css({
                pt: 8,
                textStyle: 'Text/S/Regular',
                borderTop: '1px solid',
                borderColor: 'border.default.level-1',
                color: 'content.default.level-1',
              })}
            >
              {usdBalanceEmptyThreshold} USDT minimum is required for Premium Earn
            </div>
          </div>
          <div
            className={css({
              textStyle: 'Text/L/Regular',
              color: 'content.default.level-3',
            })}
          >
            Deposit USDT and move to Premium Earn to start earning {pusdtBaseAprPct.toString()}% base APR.
          </div>
        </div>
      </Modal.Content>
      <Modal.Footer>
        <Button hierarchy='secondary' color='neutral' size='large' label='Not now' onClick={onClose} />
        <Button
          size='large'
          label='Deposit now'
          onClick={() => {
            openWalletModal('deposit', { defaultCurrency: UsdtCurrency });
            onClose();
          }}
        />
      </Modal.Footer>
    </Modal>
  );
};

export default AccessPremiumEarnModal;
